export type WalletLike = {
  balance?: string | number;
  blocked_balance?: string | number;
  available_balance?: string | number;
  wallet_balance?: string | number;
  current_balance?: string | number;
  amount?: string | number;
  currency?: string;
  currency_code?: string;
  currency_value?: string;
  name?: string;
  number?: string | number;
  wallet_number?: string | number;
  wallet_no?: string | number;
  account_number?: string | number;
  id?: string | number;
};

export const formatAmount = (amount: string | number): string => {
  amount = amount.toString();
  let parts = amount.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

export function truncateFileName(filename: string) {
  if (filename.length > 15) {
    let start = filename.substring(0, 15);
    let end = filename.substring(filename.length - 5, filename.length);
    return start + "..." + end;
  }
}

export function capitalizeFirstLetter(string: string) {
  if (string.length === 0) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const getButtonText = (order: {
  trade_role: string;
  confirmed_by_buyer: boolean;
  confirmed_by_seller: boolean;
  status: string;
  is_initiator: boolean;
  is_accepted: boolean;
}) => {
  if (order?.status === "Completed") {
    return "View settlement";
  }

  if (order?.status === "Failed" || order?.status === "Conflict") {
    return "Review flow";
  }

  return "View flow";
};

export const getWalletNumber = (wallet?: WalletLike | null): string => {
  const number =
    wallet?.number ??
    wallet?.wallet_number ??
    wallet?.wallet_no ??
    wallet?.account_number ??
    wallet?.id;

  return number == null ? "" : String(number).trim();
};

export const getWalletLabel = (wallet?: WalletLike | null, index?: number): string => {
  return (
    wallet?.currency_value ??
    wallet?.currency ??
    wallet?.currency_code ??
    wallet?.name ??
    `Wallet ${typeof index === "number" ? index + 1 : ""}`.trim()
  );
};

const CURRENCY_NAME_TO_CODE: Record<string, string> = {
  "GHANAIAN CEDI": "GHS",
  "GHANA CEDI": "GHS",
  CEDI: "GHS",
  GHS: "GHS",
  "KENYAN SHILLING": "KES",
  "KENYA SHILLING": "KES",
  SHILLING: "KES",
  KES: "KES",
  "AKL LUMI": "AKL",
  LUMI: "AKL",
  AKL: "AKL",
  "US DOLLAR": "USD",
  USD: "USD",
  EURO: "EUR",
  EUR: "EUR",
  "BRITISH POUND": "GBP",
  GBP: "GBP",
  "NIGERIAN NAIRA": "NGN",
  NAIRA: "NGN",
  NGN: "NGN",
  "SOUTH AFRICAN RAND": "ZAR",
  RAND: "ZAR",
  ZAR: "ZAR",
  "JAPANESE YEN": "JPY",
  YEN: "JPY",
  JPY: "JPY",
  "UNITED STATES DOLLAR": "USD",
};

export const getWalletCurrencyCode = (wallet?: WalletLike | null): string => {
  const code = wallet?.currency_code || wallet?.currency || "";
  if (code.length <= 4) return code.toUpperCase();
  const label = (wallet?.currency_value || wallet?.currency || wallet?.name || "").toUpperCase().trim();
  return CURRENCY_NAME_TO_CODE[label] || code.toUpperCase();
};

export const getWalletBalance = (wallet?: WalletLike | null): string => {
  const balance =
    wallet?.balance ??
    wallet?.available_balance ??
    wallet?.wallet_balance ??
    wallet?.current_balance ??
    wallet?.amount ??
    0;
  const blocked = Number(wallet?.blocked_balance ?? 0);
  const available = Number(balance) - blocked;

  return formatAmount(Math.max(available, 0));
};

export const getWalletTotalBalance = (wallet?: WalletLike | null): string => {
  const balance =
    wallet?.balance ??
    wallet?.available_balance ??
    wallet?.wallet_balance ??
    wallet?.current_balance ??
    wallet?.amount ??
    0;

  return formatAmount(balance);
};

export const getWalletBlockedBalance = (wallet?: WalletLike | null): number => {
  return Number(wallet?.blocked_balance ?? 0);
};

export const sanitizeWalletNumber = (value?: string | null): string => {
  if (!value) {
    return "";
  }

  return value.replace(/[^\d]/g, "");
};

export const findWalletByNumber = <T extends WalletLike>(
  wallets?: T[] | null,
  walletNumber?: string | null,
) => {
  if (!wallets || !walletNumber) {
    return null;
  }

  return wallets.find((wallet) => getWalletNumber(wallet) === walletNumber) ?? null;
};

export const normalizeWalletList = <T extends WalletLike>(payload?: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const candidateLists = [
    (payload as { wallets?: unknown }).wallets,
    (payload as { results?: unknown }).results,
    (payload as { data?: unknown }).data,
    (payload as { items?: unknown }).items,
  ];

  for (const candidate of candidateLists) {
    if (Array.isArray(candidate)) {
      return candidate as T[];
    }
  }

  return [];
};
